import { useContext, useEffect, useState } from "react";
import {
  Deployment,
  ServiceStack,
  useDeployMyEcsFargateStackMutation,
  useGetMyEcsServiceStackStatusQuery,
  useGetMyEnvFeaturesStackStatusQuery,
  useGetMyGitConnectionsQuery,
  useRemoveMyEcsFargateStackMutation,
  useWhoAmIQuery,
} from "../../../services/backend_gateway/__generated__/backend_gateway-types";
import { DeployEcsServiceComponent } from "./component";
import { BgwContext } from "../../../contexts/backend_gateway/context";
import { AlertsContext } from "../../../contexts/alerts/context";
import { AlertType } from "../../../contexts/alerts/type";
import { FeaturesContext } from "../../../contexts/features/context";

export const DeployEcsServiceContainer = (props: {
  deployment: Deployment;
  stack: ServiceStack;
}) => {
  const { deployment, stack } = props;
  const [queryError, setQueryError] = useState<string[]>([]);
  const [mutationInProgress, setMutationInProgress] = useState<boolean>(false);
  const removeQueryError = (i: number) => {
    setQueryError((e) => {
      const newErrors = [...e];
      newErrors.splice(i, 1);
      return newErrors;
    });
  };

  const { bgwService } = useContext(BgwContext);
  const { addAlert } = useContext(AlertsContext);
  const { apiV1Enabled, httpApiEnabled } = useContext(FeaturesContext);
  // NOTE: for the time being this is hardcoded:
  // WS API is hard if possible to integrate with ECS
  const wsApiEnabled = false;

  const { data: getMyGitConnectionsData, loading: getMyGitConnectionsLoading } =
    useGetMyGitConnectionsQuery({
      client: bgwService.getClient(),
      variables: {
        deploymentId: deployment.id,
      },
      onError: (error) => {
        if (error?.message !== undefined) {
          addAlert({
            text: error.message!,
            type: AlertType.WARNING,
          });
        }
      },
    });

  const {
    data: getMyEnvFeaturesStackStatusData,
    loading: getMyEnvFeaturesStackStatusLoading,
  } = useGetMyEnvFeaturesStackStatusQuery({
    client: bgwService.getClient(),
    variables: {
      deploymentId: deployment.id,
    },
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });

  const envFeaturesParams = JSON.parse(
    getMyEnvFeaturesStackStatusData?.getMyEnvFeaturesStackStatus
      ?.parametersJSON || "{}"
  );
  // const hasFullDomainName = envFeaturesParams.FullDomainName !== "";
  const envLogRetentionDays =
    parseInt(envFeaturesParams.LogRetentionDays) || 365;
  const envRestApiV1Enabled = envFeaturesParams.RestApiV1Enabled === "True";
  const envHttpApiV2Enabled = envFeaturesParams.HttpApiV2Enabled === "True";
  const envVpcEnabled = envFeaturesParams.VpcEnabled === "True";
  const envCognitoEnabled = envFeaturesParams.CognitoEnabled === "True";

  const {
    data: getMyEcsServiceStackStatusData,
    loading: getMyEcsServiceStackStatusLoading,
    error: getMyEcsServiceStackStatusError,
    refetch: getMyEcsServiceStackStatusRefetch,
  } = useGetMyEcsServiceStackStatusQuery({
    client: bgwService.getClient(),
    variables: {
      deploymentId: deployment.id,
      pipelineId: stack.id,
    },
  });

  useEffect(() => {
    if ((getMyEcsServiceStackStatusError?.message || null) !== null) {
      setQueryError((e) => [...e, getMyEcsServiceStackStatusError!.message]);
    }
  }, [getMyEcsServiceStackStatusError]);

  const [
    deployMyEcsFargateStackMutation,
    { loading: deployMyEcsFargateStackLoading },
  ] = useDeployMyEcsFargateStackMutation({
    client: bgwService.getClient(),
  });

  const deployEcsService = (
    instanceCount: number,
    gitConnectionArn: string,
    gitRepositoryId: string,
    gitBranch: string,
    buildspecPath: string,
    dockerfilePath: string,
    runtimeJson: string,
    buildtimeJson: string,
    ecsTaskCpu: number,
    ecsTaskRam: number,
    servicePort: number,
    healthCheckPath: string,
    healthCheckPort: number,
    logRetentionDays: number,
    alarmsEnabled: boolean,
    apiResourceEnabled: boolean,
    apiV2ResourceEnabled: boolean,
    apiKeyRequired: boolean,
    iamAuthRequired: boolean,
    cicdManualApproval: boolean,
    mapToRootPath: boolean,
    stickySessionEnabled: boolean,
    stickySessionType: string,
    autoscaleEnabled: boolean,
    maxInstanceCount: number
  ) => {
    if (deployment.version === "EnvFeatures" && !envVpcEnabled) {
      addAlert({
        text: "VPC has to be enabled in the environment features.",
        type: AlertType.WARNING,
      });
      return;
    }
    if (
      deployment.version === "EnvFeatures" &&
      !envRestApiV1Enabled &&
      (apiResourceEnabled || apiKeyRequired)
    ) {
      addAlert({
        text: "Rest API V1 has to be enabled in the environment features.",
        type: AlertType.WARNING,
      });
      return;
    }
    if (
      deployment.version === "EnvFeatures" &&
      !envHttpApiV2Enabled &&
      apiV2ResourceEnabled
    ) {
      addAlert({
        text: "Http API V2 has to be enabled in the environment features.",
        type: AlertType.WARNING,
      });
      return;
    }
    if (
      deployment.version === "EnvFeatures" &&
      !envCognitoEnabled &&
      iamAuthRequired
    ) {
      addAlert({
        text: "For IAM authentication, you might want to enable Cognito in the environment features.",
        type: AlertType.WARNING,
      });
    }
    setMutationInProgress(true);
    deployMyEcsFargateStackMutation({
      variables: {
        stackDeploymentInput: {
          id: stack.id,
          deploymentId: deployment.id,
          instanceCount,
          gitConnectionArn,
          gitRepositoryId,
          gitBranch,
          buildspecPath,
          dockerfilePath,
          runtimeJson,
          buildtimeJson,
          ecsTaskCpu,
          ecsTaskRam,
          servicePort,
          healthCheckPath,
          healthCheckPort,
          logRetentionDays,
          alarmsEnabled,
          apiResourceEnabled,
          apiV2ResourceEnabled,
          apiKeyRequired,
          iamAuthRequired,
          cicdManualApproval,
          mapToRootPath,
          stickySessionEnabled,
          stickySessionType,
          autoscaleEnabled,
          maxInstanceCount,
        },
      },
      // update(cache, { data }) {
      //   cache.modify({
      //     fields: {
      //       getMyEcsServiceStackStatus(existingnewStackStatuses = []) {
      //         const newStackStatuskRef = cache.writeFragment({
      //           data: data?.deployMyEcsFargateStack,
      //           fragment: gql`
      //             fragment NewStackStatus on StackStatus {
      //               status
      //               parametersJSON
      //             }
      //           `,
      //         });
      //         return [...existingnewStackStatuses, newStackStatuskRef];
      //       },
      //     },
      //   });
      // },
      onCompleted: (data) => {
        getMyEcsServiceStackStatusRefetch();
        setMutationInProgress(false);
      },
      onError: (error) => {
        setQueryError((e) => [
          ...e,
          `Failed to deploy stack to ${deployment.id}.`,
        ]);
        console.error(error);
        setMutationInProgress(false);
      },
    });
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (
        (
          getMyEcsServiceStackStatusData?.getMyEcsServiceStackStatus?.status ||
          ""
        ).indexOf("IN_PROGRESS") > -1
      ) {
        getMyEcsServiceStackStatusRefetch();
      }
    }, 8000);
    return () => clearInterval(intervalId);
  }, [getMyEcsServiceStackStatusData, getMyEcsServiceStackStatusRefetch]);

  const [removeMyEcsFargateStack, { loading: removeMyEcsFargateStackLoading }] =
    useRemoveMyEcsFargateStackMutation({
      client: bgwService.getClient(),
    });

  const removeEcsService = () => {
    setMutationInProgress(true);
    removeMyEcsFargateStack({
      variables: {
        stackId: null,
        deploymentId: deployment.id,
        pipelineId: stack.id,
      },
      onCompleted: () => {
        getMyEcsServiceStackStatusRefetch();
        setMutationInProgress(false);
      },
      onError: (error) => {
        setQueryError((e) => [
          ...e,
          `Failed to remove stack from ${deployment.id}.`,
        ]);
        console.error(error);
        setMutationInProgress(false);
      },
    });
  };

  const { data: whoAmIData, loading: whoAmILoading } = useWhoAmIQuery({
    client: bgwService.getClient(),
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });
  const amIRoot = whoAmIData?.whoAmI?.role === "ROOT" || false;

  return (
    <DeployEcsServiceComponent
      loading={
        getMyGitConnectionsLoading ||
        getMyEnvFeaturesStackStatusLoading ||
        getMyEcsServiceStackStatusLoading ||
        deployMyEcsFargateStackLoading ||
        removeMyEcsFargateStackLoading ||
        mutationInProgress ||
        whoAmILoading
      }
      stack={stack}
      deployment={deployment}
      stackStatus={
        getMyEcsServiceStackStatusData?.getMyEcsServiceStackStatus || null
      }
      gitConnectionArns={getMyGitConnectionsData?.getMyGitConnections || []}
      queryError={queryError}
      removeQueryError={removeQueryError}
      deployEcsService={deployEcsService}
      refetchStackStatus={() => {
        getMyEcsServiceStackStatusRefetch();
      }}
      removeEcsService={removeEcsService}
      amIRoot={amIRoot}
      apiV1Enabled={apiV1Enabled}
      httpApiEnabled={httpApiEnabled}
      wsApiEnabled={wsApiEnabled}
      envLogRetentionDays={envLogRetentionDays}
      envVpcEnabled={deployment.version !== "EnvFeatures" || envVpcEnabled}
      envRestApiV1Enabled={
        deployment.version !== "EnvFeatures" || envRestApiV1Enabled
      }
      envHttpApiV2Enabled={
        deployment.version !== "EnvFeatures" || envHttpApiV2Enabled
      }
      envCognitoEnabled={
        deployment.version !== "EnvFeatures" || envCognitoEnabled
      }
    />
  );
};
